<template>
  <div class="int-root">

    <!-- LIQ-2656: per-currency totals over whatever the Invoice view is
         currently filtered/searched to (see InvoiceViewTable.vue's
         summary-changed emit). They keep their last values while the
         Itemized view is shown, since InvoiceViewTable unmounts then. -->
    <div v-if="!hideSummaryCards" class="int-summary-cards">
      <CurrencySummaryCard
        title="Total invoiced"
        headline="Invoiced"
        :by-currency="totalsByCurrency"
        amount-key="invoiced"
        :metrics="[{ key: 'invoice_count', label: 'No. of invoices' }]"
      />
      <CurrencySummaryCard title="Paid" headline="Paid" :by-currency="totalsByCurrency" amount-key="paid" />
      <CurrencySummaryCard
        title="Outstanding"
        headline="Unpaid"
        :by-currency="totalsByCurrency"
        amount-key="outstanding"
      />
      <CurrencySummaryCard title="Due" headline="Past due date" :by-currency="totalsByCurrency" amount-key="due" />
    </div>

    <!-- View toggle + export -->
    <div class="int-toolbar">
      <div class="int-toolbar__label">{{ activeView === 'invoice' ? 'Invoice view' : 'Itemized view' }}</div>
      <div class="int-toolbar__actions">
        <b-button size="sm" variant="outline-secondary" :disabled="exporting" @click="handleExport">
          <b-spinner v-if="exporting" small class="mr-1" />
          ↓ Export {{ activeView === 'invoice' ? 'Invoices' : 'Charges' }}
        </b-button>
        <div class="int-view-toggle">
          <button
            type="button"
            class="int-view-toggle__btn"
            :class="{ 'int-view-toggle__btn--active': activeView === 'invoice' }"
            @click="activeView = 'invoice'"
          >
            Invoice view
          </button>
          <button
            type="button"
            class="int-view-toggle__btn"
            :class="{ 'int-view-toggle__btn--active': activeView === 'itemized' }"
            @click="activeView = 'itemized'"
          >
            Itemized view
          </button>
        </div>
      </div>
    </div>

    <!-- Each view fetches its own page of data server-side (pagination/
         filter/sort) — see InvoiceViewTable.vue / ItemizedInvoicesTable.vue. -->
    <InvoiceViewTable
      v-if="activeView === 'invoice'"
      ref="invoiceTableRef"
      :with-totals="!hideSummaryCards"
      @summary-changed="handleSummaryChanged"
    />
    <ItemizedInvoicesTable v-else ref="itemizedTableRef" />

  </div>
</template>

<script>
import { defineComponent, ref, computed, getCurrentInstance } from '@vue/composition-api'

import InvoiceViewTable from './InvoiceViewTable.vue'
import ItemizedInvoicesTable from './ItemizedInvoicesTable.vue'
import CurrencySummaryCard from './CurrencySummaryCard.vue'
import { resetInvoiceFilterOptions } from './useInvoiceFilterOptions'

export default defineComponent({
  name: 'InvoicingTab',

  components: {
    CurrencySummaryCard,
    InvoiceViewTable,
    ItemizedInvoicesTable,
  },

  props: {
    hideSummaryCards: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const proxy = getCurrentInstance().proxy

    // The filter-option cache lives for one visit to this page: invoices
    // created since the last visit may bring a new TPA or currency, which the
    // multi-selects could otherwise not offer until a browser reload.
    resetInvoiceFilterOptions()

    const activeView = ref('invoice')

    // {currency: {invoice_count, invoiced, paid, outstanding, due}} for the
    // Invoice view's current filter/search, from Lincoln's InvoiceListView.
    const totalsByCurrency = ref({})

    const handleSummaryChanged = ({ totalsByCurrency: totals }) => {
      totalsByCurrency.value = totals || {}
    }

    // Every currency goes to every card: CurrencySummaryCard hides a currency
    // whose figure for its amount key is 0 from the switch and bar, while the
    // "All" invoice count still includes that currency's invoices.

    // Only one of these is ever mounted at a time (v-if/v-else on
    // activeView) — the other ref is simply null.
    const invoiceTableRef = ref(null)
    const itemizedTableRef = ref(null)

    const activeTableRef = () => (activeView.value === 'invoice' ? invoiceTableRef.value : itemizedTableRef.value)

    const exporting = computed(() => {
      const tableRef = activeTableRef()
      return !!(tableRef && tableRef.exporting)
    })

    const handleExport = async () => {
      const tableRef = activeTableRef()
      if (!tableRef) return
      try {
        await tableRef.exportXlsx()
      } catch (e) {
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to export. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      }
    }

    return {
      activeView,
      handleSummaryChanged,
      totalsByCurrency,

      invoiceTableRef,
      itemizedTableRef,
      exporting,
      handleExport,
    }
  },
})
</script>

<style scoped>
.int-root { padding: 0; }

.int-summary-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}
@media (max-width: 1200px) {
  .int-summary-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
  .int-summary-cards { grid-template-columns: minmax(0, 1fr); }
}

.int-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}
.int-toolbar__label { font-size: 13px; font-weight: 700; color: #32325d; }
.int-toolbar__actions { display: flex; align-items: center; gap: 10px; }

.int-view-toggle {
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #1c4a50;
}
.int-view-toggle__btn {
  border: none;
  background: #fff;
  color: #1c4a50;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.int-view-toggle__btn--active {
  background: #1c4a50;
  color: #fff;
}
.int-view-toggle__btn:not(.int-view-toggle__btn--active):hover {
  background: #eef4f4;
}
</style>
